import { createFileRoute } from '@tanstack/react-router';
import { HealthWorkoutsScreen } from '../../screens/health/HealthWorkouts';

export type WorkoutsTab = 'today' | 'phase' | 'library' | 'planned' | 'history';
export type LibrarySub = 'workouts';

/**
 * UUID v1-v5 shape check — good enough to reject garbage query strings
 * without importing a full uuid validator for one field.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Search params drive the sub-tab so the browser back/forward buttons walk
 * the user's path inside the Workouts area (Today → Library → History → …)
 * instead of teleporting them all the way out to whichever page they came
 * from. Both fields are *optional* in the type so callers building a Link
 * to /health/workouts don't have to supply them — falling back to "today"
 * is handled by the consumer.
 */
interface WorkoutsSearch {
  tab?: WorkoutsTab;
  sub?: LibrarySub;
  /**
   * Pins a specific session into the Today tracker, overriding the usual
   * preference heuristic (pinned state > in-progress > today's planned >
   * today's completed). Set by the sidebar "Active workout" item and the
   * multi-session chooser — see HealthWorkouts.tsx.
   */
  session?: string;
  /**
   * Opens the active-session chooser on the Today tab instead of jumping
   * straight into a tracker — used when the sidebar has more than one
   * active session and can't pick one for the user.
   */
  choose?: boolean;
}

export const Route = createFileRoute('/health/workouts')({
  component: HealthWorkoutsScreen,
  validateSearch: (raw: Record<string, unknown>): WorkoutsSearch => {
    const result: WorkoutsSearch = {};
    const tab = raw['tab'];
    if (
      tab === 'today' ||
      tab === 'phase' ||
      tab === 'library' ||
      tab === 'planned' ||
      tab === 'history'
    ) {
      result.tab = tab;
    }
    const sub = raw['sub'];
    if (sub === 'workouts') result.sub = sub;
    const session = raw['session'];
    if (typeof session === 'string' && UUID_RE.test(session)) {
      result.session = session;
    }
    if (raw['choose'] === true || raw['choose'] === '1' || raw['choose'] === 'true') {
      result.choose = true;
    }
    return result;
  },
});
